import { Option, Schema as S } from "effect";
import { Field } from "foldkit/fieldValidation";
import { defineTaggedUnion } from "foldkit/schema";
import * as Relationships from "../../widgets/relationships";

import { apiData } from "../../api/asyncData";
import { Entry } from "../../api/schemas/entries";
import { BacklinkList } from "../../api/schemas/notes";
import {
	ProfileImageExists,
	RecordHistoryEntry,
	RecordSummary,
} from "../../api/schemas/records";
import { TaskPage } from "../../api/schemas/tasks";
import * as AddEntry from "../../widgets/addEntry";
import * as Attributes from "../../widgets/attributes";
import * as Tags from "../../widgets/tags";

/** Every fetched page flattened, as v1's infinite query did. */
export const Entries = S.Struct({
	items: S.Array(Entry),
	nextCursor: S.Option(S.Number),
});
export type Entries = typeof Entries.Type;

export const History = S.Struct({
	items: S.Array(RecordHistoryEntry),
	nextCursor: S.Option(S.Number),
});
export type History = typeof History.Type;
export type HistoryEntry = RecordHistoryEntry;

/** `tasks` and `notes` exist only on a person while `a2aContextEnabled`; ids `${tab}-panel` are page-unique. */
export const Tab = S.Literals(["entries", "history", "tasks", "notes"]);
export type Tab = typeof Tab.Type;

/** v1 `RecordProfileEditForm` in edit mode: reserved keys as fields, the rest in the attribute editor. */
export const EditForm = S.Struct({
	name: Field(S.String),
	subtitle: Field(S.String),
	email: Field(S.String),
	description: Field(S.String),
	tags: S.Array(S.String),
	tagBox: Tags.Model,
	tagError: S.Option(S.String),
	attributes: S.Array(Attributes.Row),
	error: S.Option(S.String),
});
export type EditForm = typeof EditForm.Type;

/** v1 `ProfileImageUploader` stages: pick a file, crop it, preview the blob. */
export const ImageStage = defineTaggedUnion({
	Pick: {},
	Crop: { file: S.instanceOf(File) },
	Preview: { blob: S.instanceOf(Blob) },
});
export type ImageStage = typeof ImageStage.Type;

export const ImageModal = S.Struct({
	stage: ImageStage,
	uploading: S.Boolean,
	uploaded: S.Boolean,
	error: S.Option(S.String),
});
export type ImageModal = typeof ImageModal.Type;

export const emptyImageModal: ImageModal = {
	stage: ImageStage.Pick(),
	uploading: false,
	uploaded: false,
	error: Option.none(),
};

export const Model = S.Struct({
	page: S.Literal("Record"),
	relationships: Relationships.Model,
	nodeId: S.String,
	record: apiData(RecordSummary).schema,
	entries: apiData(Entries).schema,
	image: apiData(ProfileImageExists).schema,
	tab: Tab,
	history: apiData(History).schema,
	/** v1 `HistoryVersionCard` `isExpanded`, keyed by `${validFrom}-${index}`. */
	expandedHistory: S.Array(S.String),
	/** `Some` while the profile card is in edit mode. */
	edit: S.Option(EditForm),
	saving: S.Boolean,
	/** `Some` while the image modal is open (edit mode only). */
	imageModal: S.Option(ImageModal),
	addEntry: AddEntry.Model,
	/** U8: a failed cadence or pause write; cleared on the next attempt. */
	cadenceError: S.Option(S.String),
	/** L5: the Agent link input while edited (`None` shows the stored link), and a failed write. */
	agentLinkDraft: S.Option(S.String),
	agentLinkError: S.Option(S.String),
	/** U12: tasks about this person and notes linking here; live data, never `asOf`. */
	tasks: apiData(TaskPage).schema,
	/** When `tasks` was read (Clock millis); 0 before the first read. */
	tasksReadAt: S.Number,
	backlinks: apiData(BacklinkList).schema,
});
export type Model = typeof Model.Type;
